import type { AlternativesEntry } from '~/composables/useAlternativesEntry'
import { useAlternativesOverlay } from '~/composables/useAlternativesOverlay'
import { useAlternativesUrl } from '~/composables/useAlternativesUrl'
import type { AlternativesNavigationTarget } from '~/composables/useAlternativesUrl'

/**
 * Shared "explore alternatives" button wiring: visibility, nav target, and
 * click handling (side effect + overlay-opening), used by all three
 * alternatives-button components.
 */
export function useAlternativesButton(
  entry: MaybeRefOrGetter<AlternativesEntry | null | undefined>,
  onClick?: () => void,
) {
  const { alternativesUrl } = useAlternativesUrl()
  const localePath = useLocalePath()
  const overlay = useAlternativesOverlay()
  const route = useRoute()

  const alternatives = computed(() => toValue(entry))

  // Hide navigate CTAs that resolve to the current page (e.g. category PLP
  // fallback → own subcategory); otherwise the link is a no-op.
  function isCurrentPage(target: AlternativesNavigationTarget): boolean {
    const decodePath = (path: string) => {
      try {
        return decodeURIComponent(path)
      }
      catch {
        return path
      }
    }
    const normalize = (path: string) => decodePath(path).replace(/\/+$/, '') || '/'
    if (normalize(localePath(target.path)) !== normalize(route.path)) return false

    const queryEntries = Object.entries(target.query)
      .filter((pair): pair is [string, string] => pair[1] !== undefined)
    return queryEntries.every(([key, value]) => route.query[key] === value)
  }

  const pointsAtCurrentPage = computed(() =>
    alternatives.value?.mode === 'navigate' && isCurrentPage(alternatives.value.target),
  )

  const isVisible = computed(() => alternatives.value != null && !pointsAtCurrentPage.value)
  const navTo = computed(() =>
    alternatives.value?.mode === 'navigate'
      ? localePath(alternativesUrl(alternatives.value.target))
      : undefined,
  )

  function handleClick() {
    onClick?.()
    if (alternatives.value?.mode === 'overlay') {
      overlay.open(alternatives.value.source)
    }
  }

  return {
    isVisible,
    navTo,
    handleClick,
  }
}
